import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { DISCOVERY_RELAYS, discoverySignal } from '@/lib/appRelays';
import { BUSINESS_KIND, BUSINESS_TAG, parseBusinessEvent } from '@/lib/business';
import type { Merchant } from '@/lib/merchants';

/**
 * Fetch all direct BCH Local listings (kind 30499 tagged source=bch-local),
 * keeping the latest per pubkey. The admin panel uses this to review and
 * approve them; approval status is layered in by the panel via
 * useApprovedListings.
 */
export function useDirectListings() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nostr', 'direct-listings'],
    queryFn: async (c): Promise<Merchant[]> => {
      const events = await nostr.group(DISCOVERY_RELAYS).query(
        [{ kinds: [BUSINESS_KIND], '#t': [BUSINESS_TAG], limit: 500 }],
        { signal: discoverySignal(c.signal) },
      );

      const latestByPubkey = new Map<string, (typeof events)[number]>();
      for (const event of events) {
        const existing = latestByPubkey.get(event.pubkey);
        if (!existing || event.created_at > existing.created_at) {
          latestByPubkey.set(event.pubkey, event);
        }
      }

      const listings: Merchant[] = [];
      for (const event of latestByPubkey.values()) {
        const merchant = parseBusinessEvent(event);
        // Only direct (app-originated) listings are managed here.
        if (merchant && merchant.source === 'bch-local') {
          listings.push(merchant);
        }
      }
      return listings;
    },
    staleTime: 60 * 1000,
  });
}
